import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { loadDocumentFromBase64, setDocumentProperty } from '../services/wordHelper';
import type { TemplateContext } from '../App';
import { getAuthHeaders, API_URL } from '../services/auth';
import './OpenTemplate.css';
const RECENT_KEY = 'dp_recentTemplates';
const FAVORITES_KEY = 'dp_favoriteTemplates';
const MAX_RECENT = 5;

/* ── Types ── */

interface Template {
  id: number;
  name: string;
  // Cross-env stable identity (UUID). Returned by the templates list endpoint
  // since the PublicId migration; older deployments may omit it, in which
  // case dp_publicId simply won't be written for that template.
  publicId?: string;
  description?: string;
  status?: string;
  categoryId?: number | null;
  categoryName?: string;
  documentType?: string | null;
  updatedAt?: string;
  checkedOutBy?: string | null;
  hasDraft?: boolean;
  draftVersion?: number;
  publishedVersion?: number;
}

interface Category {
  id: number;
  name: string;
}

interface RecentEntry {
  id: number;
  name: string;
  openedAt: string;
}

type SortOption = 'updated' | 'name-asc' | 'name-desc' | 'category';
type FilterMode = 'all' | 'favorites' | number; // number = categoryId

/* ── Helpers ── */

function normalizeTemplate(raw: Record<string, unknown>): Template {
  return {
    id: (raw.id ?? raw.Id) as number,
    name: (raw.name ?? raw.Name ?? '') as string,
    // publicId MUST be carried through — the Edit flow's
    // `if (template.publicId)` guard depends on it to stamp dp_publicId
    // into the document. Dropping it here silently makes every Edit
    // produce a "legacy" binding (the field is on the Template interface,
    // so the omission compiled clean and went unnoticed).
    publicId: (raw.publicId ?? raw.PublicId ?? undefined) as string | undefined,
    description: (raw.description ?? raw.Description ?? '') as string,
    status: (raw.status ?? raw.Status ?? '') as string,
    categoryId: (raw.categoryId ?? raw.CategoryId ?? null) as number | null,
    categoryName: (raw.categoryName ?? raw.CategoryName ?? raw.category ?? raw.Category ?? '') as string,
    updatedAt: (raw.updatedAt ?? raw.UpdatedAt ?? '') as string,
    checkedOutBy: (raw.checkedOutBy ?? raw.CheckedOutBy ?? null) as string | null,
    documentType: (raw.documentType ?? raw.DocumentType ?? null) as string | null,
    hasDraft: (raw.hasDraft ?? raw.HasDraft ?? false) as boolean,
    draftVersion: (raw.draftVersion ?? raw.DraftVersion ?? undefined) as number | undefined,
    publishedVersion: (raw.publishedVersion ?? raw.PublishedVersion ?? undefined) as number | undefined,
  };
}

function normalizeCategory(raw: Record<string, unknown>): Category {
  return {
    id: (raw.id ?? raw.Id) as number,
    name: (raw.name ?? raw.Name ?? '') as string,
  };
}

function relativeTime(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const now = Date.now();
  const diff = now - date.getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function loadRecent(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecent(entries: RecentEntry[]) {
  localStorage.setItem(RECENT_KEY, JSON.stringify(entries.slice(0, MAX_RECENT)));
}

function addToRecent(template: Template) {
  const entries = loadRecent().filter(e => e.id !== template.id);
  entries.unshift({ id: template.id, name: template.name, openedAt: new Date().toISOString() });
  saveRecent(entries);
}

function loadFavoriteIds(): Set<number> {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    return raw ? new Set(JSON.parse(raw) as number[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveFavoriteIds(ids: Set<number>) {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify([...ids]));
}

/* ── Component ── */

type Action = 'edit' | 'copy' | null;

interface OpenTemplateProps {
  onSaved: (template: TemplateContext) => void;
}

export function OpenTemplate({ onSaved }: OpenTemplateProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('updated');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(loadFavoriteIds);
  const [recentEntries, setRecentEntries] = useState<RecentEntry[]>(loadRecent);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Confirm dialog state
  const [confirmTemplate, setConfirmTemplate] = useState<Template | null>(null);
  const [confirmAction, setConfirmAction] = useState<Action>(null);
  const [copyName, setCopyName] = useState('');
  const [processing, setProcessing] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  /* ── Fetch data ── */

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/templates`, {
        headers: { ...getAuthHeaders() },
      });
      const data = await response.json();
      if (data.success && data.data) {
        const list = Array.isArray(data.data) ? data.data : (data.data.items || data.data.templates || []);
        setTemplates(list.map((r: Record<string, unknown>) => normalizeTemplate(r)));
      } else {
        setError('Failed to fetch templates.');
      }
    } catch {
      setError('Failed to fetch templates. Check API connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/categories`, {
        headers: { ...getAuthHeaders() },
      });
      const data = await response.json();
      if (data.success && data.data) {
        const list = Array.isArray(data.data) ? data.data : (data.data.items || data.data.categories || []);
        setCategories(list.map((r: Record<string, unknown>) => normalizeCategory(r)));
      }
    } catch {
      // Categories are non-critical; silently ignore
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, [fetchTemplates, fetchCategories]);

  /* ── Favorites toggle ── */

  const toggleFavorite = async (template: Template) => {
    const newIds = new Set(favoriteIds);
    const isFav = newIds.has(template.id);

    // Optimistic update
    if (isFav) {
      newIds.delete(template.id);
    } else {
      newIds.add(template.id);
    }
    setFavoriteIds(newIds);
    saveFavoriteIds(newIds);

    // Fire API call (best-effort)
    try {
      await fetch(`${API_URL}/favorites`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityType: 'Template', entityId: template.id }),
      });
    } catch {
      // Revert on failure
      if (isFav) {
        newIds.add(template.id);
      } else {
        newIds.delete(template.id);
      }
      setFavoriteIds(new Set(newIds));
      saveFavoriteIds(newIds);
    }
  };

  /* ── Template actions ── */

  // Version picker for the Edit dialog. editVersions is the full live-
  // version list fetched on Edit click; selectedVersion is whichever
  // version the user picks from the dropdown (defaults to latest).
  interface EditVersion { versionNumber: number; versionStatus: string; uploadedAt?: string; uploadedBy?: string }
  const [editVersions, setEditVersions] = useState<EditVersion[]>([]);
  const [editVersionsLoading, setEditVersionsLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<EditVersion | null>(null);

  const handleEdit = async (template: Template) => {
    if (template.checkedOutBy) {
      setError(`Template is checked out by ${template.checkedOutBy}`);
      return;
    }
    setConfirmTemplate(template);
    setConfirmAction('edit');
    setMessage(null);
    // Fetch the full version list so the dialog can offer ANY live version
    // (not just the latest Draft + currently-Published). Optimistic
    // concurrency still applies; the save path refuses stale bases with a
    // 409 and the user can pick "Save my version on top" to recover.
    setEditVersions([]);
    setSelectedVersion(null);
    setEditVersionsLoading(true);
    try {
      const resp = await fetch(`${API_URL}/templates/${template.id}/versions`, {
        headers: { ...getAuthHeaders() },
      });
      if (resp.ok) {
        const json = await resp.json();
        const list: any[] = Array.isArray(json?.data) ? json.data : [];
        // Drop soft-deleted versions; the user shouldn't be able to open
        // them. Sort newest-first so the dropdown matches a normal "most
        // recent first" mental model.
        const live: EditVersion[] = list
          .filter((v) => v.VersionStatus !== 'Deleted')
          .map((v) => ({
            versionNumber: v.VersionNumber,
            versionStatus: v.VersionStatus,
            uploadedAt: v.UploadedAt,
            uploadedBy: v.UploadedBy,
          }))
          .sort((a, b) => b.versionNumber - a.versionNumber);
        setEditVersions(live);
        // Default: latest version (top of the list), regardless of status.
        // If empty (no versions yet), leave null and the dialog renders an
        // "Open" button that's disabled.
        setSelectedVersion(live[0] ?? null);
      }
    } catch { /* silent — dialog will show empty picker */ }
    finally { setEditVersionsLoading(false); }
  };

  const handleCopy = (template: Template) => {
    setConfirmTemplate(template);
    setConfirmAction('copy');
    setCopyName(`${template.name} (Copy)`);
    setMessage(null);
  };

  const loadTemplateIntoWord = async (template: Template) => {
    setProcessing(true);
    setError(null);
    try {
      // Pick the version the user selected in the dropdown. Falls back to
      // a sensible default (latest available) when no selection is set
      // — happens if the version-list fetch failed or hasn't returned yet
      // and the user clicked Open anyway.
      let versionNumber: number;
      let versionStatus: string;
      if (selectedVersion) {
        versionNumber = selectedVersion.versionNumber;
        versionStatus = selectedVersion.versionStatus;
      } else {
        versionNumber = template.draftVersion ?? template.publishedVersion ?? (template as any).currentVersion ?? 1;
        versionStatus = template.hasDraft ? 'Draft' : (template.publishedVersion ? 'Published' : 'Draft');
      }

      // Download specific version from .NET doc-service via gateway
      const response = await fetch(`${API_URL}/doc-service/templates/${template.id}/versions/${versionNumber}/file`, {
        headers: { ...getAuthHeaders() },
      });

      if (!response.ok) {
        throw new Error('Template has no file data. Save a document first.');
      }

      // Convert binary to base64
      const blob = await response.blob();
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]); // strip data:...;base64, prefix
        };
        reader.readAsDataURL(blob);
      });

      if (base64) {
        await loadDocumentFromBase64(base64);

        await setDocumentProperty('dp_templateId', String(template.id));
        await setDocumentProperty('dp_templateName', template.name);
        await setDocumentProperty('dp_versionNumber', String(versionNumber));
        await setDocumentProperty('dp_versionStatus', String(versionStatus));
        // dp_publicId is what verify-binding actually trusts — write it so a
        // future "open this .docx from desktop" can resolve the binding even
        // if local Template.Id has been renumbered by a dev→prod promotion.
        if (template.publicId) {
          await setDocumentProperty('dp_publicId', template.publicId);
        }
        if (template.documentType) {
          await setDocumentProperty('dp_docTypeGroup', template.documentType);
          await setDocumentProperty('dp_docType', template.documentType);
        }

        // Update recent
        addToRecent(template);
        setRecentEntries(loadRecent());

        setMessage({ type: 'success', text: `Loaded "${template.name}" (${versionStatus} v${versionNumber}) for editing.` });

        setTimeout(() => {
          onSaved({
            id: template.id,
            name: template.name,
            versionNumber: typeof versionNumber === 'number' ? versionNumber : parseInt(versionNumber, 10),
            versionStatus: versionStatus as TemplateContext['versionStatus'],
            isLocked: false,
            publicId: template.publicId,
          });
        }, 1500);
      } else {
        setError('No file data returned from API.');
      }
    } catch (err: any) {
      // Surface the real failure (Office.js error code/message, fetch
      // failure, etc.) instead of the generic "Failed to load…". This
      // catch used to swallow everything, which made debugging the
      // Edit-flow impossible whenever something was off.
      const code = err?.code || err?.debugInfo?.code || '';
      const msg = err?.message || err?.debugInfo?.message || 'unknown error';
      // eslint-disable-next-line no-console
      console.error('[OpenTemplate] loadTemplateIntoWord failed:', err);
      setError(`Failed to load template: ${code ? `[${code}] ` : ''}${msg}`);
    } finally {
      setProcessing(false);
      setConfirmTemplate(null);
      setConfirmAction(null);
    }
  };

  const handleConfirmEdit = async () => {
    if (!confirmTemplate) return;
    await loadTemplateIntoWord(confirmTemplate);
  };

  const handleConfirmCopy = async () => {
    if (!confirmTemplate || !copyName.trim()) return;
    setProcessing(true);
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/templates/${confirmTemplate.id}/clone`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: copyName.trim() }),
      });
      const data = await resp.json();
      if (data.success && data.data) {
        setTemplates(prev => [...prev, normalizeTemplate(data.data)]);
        setMessage({ type: 'success', text: `Copied as "${copyName.trim()}".` });
      } else {
        setError(data.error?.message || 'Failed to copy template.');
      }
    } catch {
      setError('Failed to copy template.');
    } finally {
      setProcessing(false);
      setConfirmTemplate(null);
      setConfirmAction(null);
      setCopyName('');
    }
  };

  const handleCancel = () => {
    setConfirmTemplate(null);
    setConfirmAction(null);
    setCopyName('');
  };

  /* ── Filter + Sort ── */

  const categoryCountMap = useMemo(() => {
    const map = new Map<number, number>();
    templates.forEach(t => {
      if (t.categoryId) {
        map.set(t.categoryId, (map.get(t.categoryId) || 0) + 1);
      }
    });
    return map;
  }, [templates]);

  const favoritesCount = useMemo(() => {
    return templates.filter(t => favoriteIds.has(t.id)).length;
  }, [templates, favoriteIds]);

  const filteredAndSorted = useMemo(() => {
    let result = [...templates];

    // Search filter (client-side, debounced)
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(t => t.name.toLowerCase().includes(q));
    }

    // Category / favorites filter
    if (filterMode === 'favorites') {
      result = result.filter(t => favoriteIds.has(t.id));
    } else if (typeof filterMode === 'number') {
      result = result.filter(t => t.categoryId === filterMode);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'category':
          return (a.categoryName || '').localeCompare(b.categoryName || '');
        case 'updated':
        default: {
          const da = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const db = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return db - da; // newest first
        }
      }
    });

    return result;
  }, [templates, debouncedSearch, filterMode, sortBy, favoriteIds]);

  // Recent templates that exist in the loaded list
  const recentTemplates = useMemo(() => {
    const idSet = new Set(templates.map(t => t.id));
    const recent = recentEntries.filter(e => idSet.has(e.id));
    return recent.map(e => templates.find(t => t.id === e.id)!).filter(Boolean);
  }, [templates, recentEntries]);

  const showRecent = recentTemplates.length > 0 && filterMode === 'all' && !debouncedSearch;

  /* ── Status badge helper ── */

  function statusBadgeClass(status?: string): string {
    switch (status?.toLowerCase()) {
      case 'active': return 'dp-ot__badge dp-ot__badge--active';
      case 'draft': return 'dp-ot__badge dp-ot__badge--draft-status';
      case 'archived': return 'dp-ot__badge dp-ot__badge--archived';
      default: return 'dp-ot__badge dp-ot__badge--draft-status';
    }
  }

  /* ── Render template card ── */

  function renderCard(template: Template) {
    const isFav = favoriteIds.has(template.id);

    return (
      <div key={template.id} className="dp-ot__card">
        {/* Favorite star */}
        <button
          className={`dp-ot__card-fav${isFav ? ' dp-ot__card-fav--active' : ''}`}
          onClick={() => toggleFavorite(template)}
          title={isFav ? 'Remove from favorites' : 'Add to favorites'}
        >
          {isFav ? '\u2605' : '\u2606'}
        </button>

        {/* Card body */}
        <div className="dp-ot__card-body">
          <span className="dp-ot__card-name">{template.name}</span>
          <div className="dp-ot__card-badges">
            {/* Category badge */}
            {template.categoryName && (
              <span className="dp-ot__badge dp-ot__badge--category">{template.categoryName}</span>
            )}

            {/* Status badge */}
            {template.status && (
              <span className={statusBadgeClass(template.status)}>{template.status}</span>
            )}

            {/* Published version */}
            {template.publishedVersion != null && (
              <span className="dp-ot__badge dp-ot__badge--published">v{template.publishedVersion}</span>
            )}

            {/* Draft indicator */}
            {template.hasDraft && template.draftVersion != null && (
              <span className="dp-ot__badge dp-ot__badge--draft">Draft v{template.draftVersion}</span>
            )}

            {/* Lock indicator */}
            {template.checkedOutBy && (
              <span className="dp-ot__badge dp-ot__badge--locked">
                <span className="dp-ot__lock-icon">{'\uD83D\uDD12'}</span> Locked by {template.checkedOutBy}
              </span>
            )}
          </div>

          {/* Last updated */}
          {template.updatedAt && (
            <span className="dp-ot__card-meta">Updated {relativeTime(template.updatedAt)}</span>
          )}
        </div>

        {/* Actions */}
        <div className="dp-ot__card-actions">
          <button
            className="dp-ot__btn dp-ot__btn--edit"
            onClick={() => handleEdit(template)}
            title={template.checkedOutBy ? `Locked by ${template.checkedOutBy}` : 'Load template for editing'}
          >
            Edit
          </button>
          <button
            className="dp-ot__btn dp-ot__btn--copy"
            onClick={() => handleCopy(template)}
            title="Create a copy of this template"
          >
            Copy
          </button>
        </div>
      </div>
    );
  }

  /* ── Main render ── */

  return (
    <div className="dp-open-template">
      {/* Toolbar: Search + Sort */}
      <div className="dp-ot__toolbar">
        <input
          type="text"
          placeholder="Search templates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="dp-ot__search-input"
        />
        <select
          className="dp-ot__sort-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          title="Sort templates"
        >
          <option value="updated">Last Updated</option>
          <option value="name-asc">Name (A-Z)</option>
          <option value="name-desc">Name (Z-A)</option>
          <option value="category">Category</option>
        </select>
      </div>

      {/* Category chips */}
      <div className="dp-ot__categories">
        <button
          className={`dp-ot__cat-chip${filterMode === 'all' ? ' dp-ot__cat-chip--active' : ''}`}
          onClick={() => setFilterMode('all')}
        >
          All <span className="dp-ot__cat-count">({templates.length})</span>
        </button>
        <button
          className={`dp-ot__cat-chip dp-ot__cat-chip--favorites${filterMode === 'favorites' ? ' dp-ot__cat-chip--active' : ''}`}
          onClick={() => setFilterMode(filterMode === 'favorites' ? 'all' : 'favorites')}
        >
          {'\u2605'} Favorites <span className="dp-ot__cat-count">({favoritesCount})</span>
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            className={`dp-ot__cat-chip${filterMode === cat.id ? ' dp-ot__cat-chip--active' : ''}`}
            onClick={() => setFilterMode(filterMode === cat.id ? 'all' : cat.id)}
          >
            {cat.name} <span className="dp-ot__cat-count">({categoryCountMap.get(cat.id) || 0})</span>
          </button>
        ))}
      </div>

      {/* Error / message */}
      {error && <div className="dp-ot__error">{error}</div>}
      {message && (
        <div className={`dp-ot__message dp-ot__message--${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Edit Confirmation with Version Picker */}
      {confirmAction === 'edit' && confirmTemplate && (
        <div className="dp-ot__confirm">
          <div className="dp-ot__confirm-content">
            <h4>Edit Template</h4>
            <p>
              Loading <strong>{confirmTemplate.name}</strong> will replace all content in the current document.
            </p>

            {/* Version picker — dropdown of every live version, newest
                first. Defaults to the latest (top of list); the user can
                pick any historical version (older drafts, the currently-
                published one, or an older Published that was later
                superseded). Two derived labels show inline so the user
                doesn't have to remember which version is "the live one":
                  • (latest)   — top of the list, regardless of status
                  • (live)     — the currently-published version */}
            <div style={{ margin: '10px 0', padding: '8px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Select version to open:</div>
              {editVersionsLoading ? (
                <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>Loading versions…</div>
              ) : editVersions.length === 0 ? (
                <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>No versions available.</div>
              ) : (
                <select
                  value={selectedVersion?.versionNumber ?? ''}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    const picked = editVersions.find((v) => v.versionNumber === n) || null;
                    setSelectedVersion(picked);
                  }}
                  style={{
                    width: '100%', fontSize: 12, padding: '4px 6px',
                    border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff',
                  }}
                >
                  {editVersions.map((v, i) => {
                    const isLatest = i === 0;
                    const isLive = confirmTemplate.publishedVersion === v.versionNumber;
                    const tags: string[] = [];
                    if (isLatest) tags.push('latest');
                    if (isLive) tags.push('live');
                    const suffix = tags.length ? ` (${tags.join(', ')})` : '';
                    return (
                      <option key={v.versionNumber} value={v.versionNumber}>
                        {v.versionStatus} v{v.versionNumber}{suffix}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>

            <div className="dp-ot__confirm-actions">
              <button
                className="dp-ot__btn dp-ot__btn--primary"
                onClick={handleConfirmEdit}
                disabled={processing}
              >
                {processing ? 'Loading...' : 'Edit'}
              </button>
              <button
                className="dp-ot__btn dp-ot__btn--cancel"
                onClick={handleCancel}
                disabled={processing}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Confirmation */}
      {confirmAction === 'copy' && confirmTemplate && (
        <div className="dp-ot__confirm">
          <div className="dp-ot__confirm-content">
            <h4>Copy Template</h4>
            <p>Create a copy of <strong>{confirmTemplate.name}</strong> with a new name.</p>
            <input
              type="text"
              className="dp-ot__copy-input"
              value={copyName}
              onChange={(e) => setCopyName(e.target.value)}
              placeholder="New template name"
            />
            <div className="dp-ot__confirm-actions">
              <button
                className="dp-ot__btn dp-ot__btn--primary"
                onClick={handleConfirmCopy}
                disabled={processing || !copyName.trim()}
              >
                {processing ? 'Copying...' : 'Copy'}
              </button>
              <button
                className="dp-ot__btn dp-ot__btn--cancel"
                onClick={handleCancel}
                disabled={processing}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template List */}
      {!confirmTemplate && (
        <div className="dp-ot__list">
          {loading && (
            <div className="dp-ot__loading">Loading templates...</div>
          )}

          {/* Recent section */}
          {!loading && showRecent && (
            <>
              <div className="dp-ot__section-header">
                <span className="dp-ot__section-icon">{'\uD83D\uDD52'}</span> Recent
              </div>
              {recentTemplates.map(t => renderCard(t))}
            </>
          )}

          {/* Main list */}
          {!loading && filteredAndSorted.length > 0 && (
            <>
              {showRecent && (
                <div className="dp-ot__section-header">
                  <span className="dp-ot__section-icon">{'\uD83D\uDCC4'}</span> All Templates
                </div>
              )}
              {filteredAndSorted.map(t => renderCard(t))}
            </>
          )}

          {!loading && filteredAndSorted.length === 0 && (
            <div className="dp-ot__empty">
              {debouncedSearch
                ? 'No templates match your search.'
                : filterMode === 'favorites'
                  ? 'No favorite templates yet. Star a template to add it here.'
                  : 'No templates available.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
